import { useInfiniteQuery, useQueries, useQuery } from '@tanstack/react-query'
import {
  POND_TYPES_KEY,
  SHRIMP_BRANDS_KEY,
  getCycleKey,
  getPondDashboardKey,
  getShrimpTypesKey,
  SPECIES_KEY,
  getBreedKey,
  getBreedBrandKey,
  getBreedProductKey,
  getBreedNameKey,
  getFeedProgramIdKey,
  getFeedProgramsKey,
  getSpeciesIdKey,
  getMarketIdKey,
  AREA_UNIT,
  getPondDashboardChartKey,
  getPondDashboardRevenueChartKey,
  getPondKey,
} from 'common/constants/queryKeys'
import useCycleApi from 'common/hooks/useCycleApi'
import useFarmApi from 'common/hooks/useFarmApi'
import usePondApi from 'common/hooks/usePondApi'
import { BaseResponse } from 'common/types/api'
import { createFarmApi } from 'core/api/apiFactory'
import { useParams } from 'react-router-dom'
import { useRecoilValue } from 'recoil'

import { buildUrlWithParams } from 'utils/url'

import {
  CYCLE_REQUEST_CONFIG,
  DASHBOARD_REQUEST_CONFIG,
  getAllPondNames,
  getBreed,
  getBreedBrand,
  getBreedProduct,
  getCycleByFarmByPond,
  getFeedPrograms,
  getFeedProgramsById,
  getMarkets,
  getMarketsById,
  getPondAreaUnits,
  getPondByFarm,
  getPondTypes,
  getShrimpBrands,
  getShrimpTypes,
  getSpecies,
  POND_REQUEST_CONFIG,
} from '.'
import { selectPondFiltersState, selectedCycleIdState } from '../stores/atom'
import { useSpeciesId } from './selector'
import {
  CompletedPondParams,
  CompletedPondResponse,
  Cycle,
  CycleHistory,
  PondDashboard,
  PondDashboardChart,
  PondDashboardFinance,
  PondDashboardRevenueChart,
  PondFilterCountResponse,
  PondInfo,
  PondResponse,
} from './types'

export const useGetPonds = () => {
  const farmApi = useFarmApi()
  const { farmId } = useParams()
  const pondFilters = useRecoilValue(selectPondFiltersState)
  const size = 9

  return useInfiniteQuery({
    queryKey: getPondKey.filter(farmId, pondFilters),
    queryFn: async ({ pageParam = 1 }) => {
      const url = buildUrlWithParams(POND_REQUEST_CONFIG.getPonds.url ?? '', {
        ...pondFilters,
        page: pageParam,
        pageSize: size,
      })

      const { data } = await farmApi.request<PondResponse>({ url })
      return data
    },
    getNextPageParam: (lastPage) => lastPage?.nextPage ?? undefined,
    enabled: !!farmId,
  })
}

export const useGetPondTypes = () => {
  return useQuery({
    queryKey: POND_TYPES_KEY,
    queryFn: async () => {
      const { data } = await getPondTypes()
      return data.data
    },
    staleTime: Infinity,
  })
}

export const useGetShrimpBrands = () => {
  return useQuery({
    queryKey: SHRIMP_BRANDS_KEY,
    queryFn: async () => {
      const { data } = await getShrimpBrands()
      return data.data
    },
    staleTime: Infinity,
  })
}

export const useGetShrimpTypes = (shrimpBrand: number) => {
  return useQuery({
    queryKey: getShrimpTypesKey(shrimpBrand),
    queryFn: async () => {
      const { data } = await getShrimpTypes(shrimpBrand)
      return data.data
    },
    staleTime: Infinity,
    enabled: shrimpBrand !== -1,
  })
}

export const useGetCycles = () => {
  const pondApi = usePondApi()
  const { pondId } = useParams()

  return useQuery({
    queryKey: getCycleKey.list(pondId),
    queryFn: async () => {
      const { data } = await pondApi.request<BaseResponse<CycleHistory[]>>(
        CYCLE_REQUEST_CONFIG.getCycles
      )
      return data.data
    },
    staleTime: Infinity,
  })
}

export const useGetCycleById = () => {
  const pondApi = usePondApi()
  const cycleId = useRecoilValue(selectedCycleIdState)

  return useQuery({
    queryKey: getCycleKey.byCycleId(cycleId || ''),
    queryFn: async () => {
      const { data } = await pondApi.request<BaseResponse<Cycle>>({
        ...CYCLE_REQUEST_CONFIG.getCycleById,
        url: `${CYCLE_REQUEST_CONFIG.getCycleById.url}/${cycleId}`,
      })
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId,
    refetchOnMount: 'always',
  })
}

export const useGetPondDashboard = () => {
  const pondApi = usePondApi()
  const cycleId = useRecoilValue(selectedCycleIdState)
  const { pondId } = useParams()

  return useQuery({
    queryKey: getPondDashboardKey.overview(pondId || '', cycleId || ''),
    queryFn: async () => {
      const { data: cycle } = await pondApi.request<BaseResponse<Cycle>>({
        ...CYCLE_REQUEST_CONFIG.getCycleById,
        url: `${CYCLE_REQUEST_CONFIG.getCycleById.url}/${cycleId}`,
      })
      const { data: pondDashboard } = await pondApi.request<BaseResponse<PondDashboard>>({
        ...DASHBOARD_REQUEST_CONFIG.getDashboard,
        url: `/cycles/${cycleId}${DASHBOARD_REQUEST_CONFIG.getDashboard.url}`,
      })
      return {
        ...pondDashboard.data,
        status: cycle?.data?.status,
        breedId: cycle?.data?.breedProduct.breedId,
      }
    },
    staleTime: Infinity,
    enabled: !!cycleId,
    refetchOnMount: 'always',
  })
}

export const useGetPondDashboardFinance = () => {
  const pondApi = usePondApi()
  const cycleId = useRecoilValue(selectedCycleIdState)
  const { pondId } = useParams()

  return useQuery({
    queryKey: getPondDashboardKey.finance(pondId || '', cycleId || ''),
    queryFn: async () => {
      const { data: cycle } = await pondApi.request<BaseResponse<Cycle>>({
        ...CYCLE_REQUEST_CONFIG.getCycleById,
        url: `${CYCLE_REQUEST_CONFIG.getCycleById.url}/${cycleId}`,
      })
      const { data: pondDashboard } = await pondApi.request<BaseResponse<PondDashboardFinance>>({
        ...DASHBOARD_REQUEST_CONFIG.getDashboardFinance,
        url: `/cycles/${cycleId}${DASHBOARD_REQUEST_CONFIG.getDashboardFinance.url}`,
      })
      return {
        ...pondDashboard.data,
        status: cycle?.data?.status,
        breedId: cycle?.data?.breedProduct.breedId,
      }
    },
    staleTime: Infinity,
    enabled: !!cycleId,
    refetchOnMount: 'always',
  })
}

export const useGetPondInfo = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)

  return useQuery({
    queryKey: getPondKey.details(cycleId ?? ''),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<PondInfo>>(
        DASHBOARD_REQUEST_CONFIG.getPondDasboard
      )
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId,
  })
}

export const useGetSpecies = () => {
  return useQuery({
    queryKey: SPECIES_KEY,
    queryFn: async () => {
      const { data } = await getSpecies()
      return data.data
    },
    staleTime: Infinity,
  })
}

export const useGetBreed = (speciesId?: number) => {
  return useQuery({
    queryKey: getBreedKey(speciesId ?? 0),
    queryFn: async () => {
      const { data } = await getBreed(speciesId ?? 0)
      return data.data
    },
    staleTime: Infinity,
    enabled: !!speciesId,
  })
}

export const useGetBreedName = (breedId?: number) => {
  const speciesId = useSpeciesId()

  return useQuery({
    queryKey: getBreedNameKey(speciesId, breedId ?? 0),
    queryFn: async () => {
      if (!speciesId || !breedId) return ''
      const { data } = await getBreed(speciesId)
      const breed = data?.data?.find((element: { id: number }) => element.id === breedId)

      return breed ? breed.name : ''
    },
    staleTime: Infinity,
    enabled: !!speciesId && !!breedId,
  })
}

export const useGetBreedBrand = (breedId: number) => {
  const speciesId = useSpeciesId()

  return useQuery({
    queryKey: getBreedBrandKey(breedId),
    queryFn: async () => {
      const { data } = await getBreedBrand({ speciesId, breedId })
      return data.data
    },
    staleTime: Infinity,
    enabled: breedId > 0,
  })
}

export const useGetBreedProduct = (breedId: number, breedBrandId: number) => {
  const speciesId = useSpeciesId()

  return useQuery({
    queryKey: getBreedProductKey(breedId, breedBrandId),
    queryFn: async () => {
      const { data } = await getBreedProduct({ speciesId, breedId, breedBrandId })
      return data.data
    },
    staleTime: Infinity,
    enabled: breedBrandId > 0,
  })
}

export const useGetFeedPrograms = () => {
  return useQuery({
    queryKey: getFeedProgramsKey(),
    queryFn: async () => {
      const { data } = await getFeedPrograms()
      return data.data
    },
    staleTime: Infinity,
  })
}

export const useGetFeedProgramsById = (feedProgramId?: number, enabled?: boolean) => {
  return useQuery({
    queryKey: getFeedProgramIdKey(feedProgramId ?? 0),
    queryFn: async () => {
      const { data } = await getFeedProgramsById(feedProgramId)
      return data.data
    },
    staleTime: Infinity,
    enabled: !!feedProgramId && !!enabled,
  })
}

export const useGetMarkets = (speciesId?: number) => {
  return useQuery({
    queryKey: getSpeciesIdKey(speciesId ?? 0),
    queryFn: async () => {
      const { data } = await getMarkets(speciesId ?? 0)
      return data.data
    },
    staleTime: Infinity,
    enabled: !!speciesId,
  })
}

export const useGetMarketsById = (marketId?: number, enabled?: boolean) => {
  return useQuery({
    queryKey: getMarketIdKey(marketId ?? 0),
    queryFn: async () => {
      const { data } = await getMarketsById(marketId ?? 0)
      return data.data
    },
    staleTime: Infinity,
    enabled: !!marketId && !!enabled,
  })
}

export const useGetPondAreaUnits = () => {
  return useQuery({
    queryKey: AREA_UNIT,
    queryFn: async () => {
      const {
        data: { data },
      } = await getPondAreaUnits()

      return data
    },
    staleTime: Infinity,
  })
}

interface UseGetCompletedPondsOptions extends CompletedPondParams {
  /** Defaults to the farm in the route. Pass another farm to list its empty ponds. */
  farmId?: string
  enabled?: boolean
}

const fetchCompletedPonds = async (farmId: string, params?: CompletedPondParams) => {
  const { data } = await createFarmApi(farmId).request<BaseResponse<CompletedPondResponse>>({
    ...POND_REQUEST_CONFIG.getCompletedPonds,
    params,
    // Other farms may be off-limits (403); the picker handles that itself.
    silentError: true,
  })
  // Backend sends `ponds: null` for a farm with no empty pond
  return { ...data.data, ponds: data.data?.ponds ?? [] }
}

export const useGetCompletedPonds = (options: UseGetCompletedPondsOptions = {}) => {
  const { farmId: routeFarmId } = useParams()
  const { farmId = routeFarmId, enabled = true, excludePondId, speciesId } = options
  const params: CompletedPondParams = { excludePondId, speciesId }

  return useQuery({
    queryKey: getPondKey.completed(farmId, params),
    queryFn: () => fetchCompletedPonds(farmId ?? '', params),
    staleTime: Infinity,
    // A 403/404 for another farm will not heal by retrying - the picker shows
    // its own error state with a manual retry instead.
    retry: false,
    enabled: enabled && !!farmId,
  })
}

/** Empty ponds of several farms at once (destination farm list of a transfer). */
export const useGetCompletedPondsByFarms = (
  farmIds: string[],
  params: CompletedPondParams,
  enabled = true
) =>
  useQueries({
    queries: farmIds.map((farmId) => ({
      queryKey: getPondKey.completed(farmId, params),
      queryFn: () => fetchCompletedPonds(farmId, params),
      staleTime: Infinity,
      retry: false,
      enabled,
    })),
  })

export const useGetPondFilterCount = () => {
  const farmApi = useFarmApi()
  const { farmId } = useParams()

  return useQuery({
    queryKey: getPondKey.filterCount(farmId),
    queryFn: async () => {
      const { data } = await farmApi.request<BaseResponse<PondFilterCountResponse>>(
        POND_REQUEST_CONFIG.filterCount
      )
      return data.data
    },
    staleTime: Infinity,
  })
}

export const useGetPondsByFarm = (farmId: string) => {
  return useQuery({
    queryKey: getPondKey.list(farmId),
    queryFn: async () => {
      const { data } = await getPondByFarm(farmId)
      return data.data
    },
    staleTime: Infinity,
    enabled: !!farmId,
  })
}

export const useGetCyclesByFarmByPond = (farmId: string, pondId: string) => {
  return useQuery({
    queryKey: getCycleKey.byFarmByPond(farmId, pondId),
    queryFn: async () => {
      const { data } = await getCycleByFarmByPond(farmId, pondId)
      return data.data
    },
    enabled: !!farmId && !!pondId,
    staleTime: Infinity,
  })
}

export const useGetPondDashboardLineChart = (params?: {
  graphs?: string
  quality?: string
  startDate?: string
  endDate?: string
}) => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)

  return useQuery({
    queryKey: [getPondDashboardChartKey(cycleId || ''), params],
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<PondDashboardChart>>({
        ...DASHBOARD_REQUEST_CONFIG.getLineChart,
        params: {
          ...params,
        },
      })
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId,
  })
}

export const useGetPondDashboardRevenueLineChart = (params?: {
  startDate?: string
  endDate?: string
}) => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)

  return useQuery({
    queryKey: [getPondDashboardRevenueChartKey(cycleId || ''), params],
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<PondDashboardRevenueChart>>({
        ...DASHBOARD_REQUEST_CONFIG.getRevenueLineChart,
        params: {
          ...params,
        },
      })
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId,
  })
}

export const useGetPondNames = (farmIdOverride?: string) => {
  const { farmId: routeFarmId } = useParams()
  const farmId = farmIdOverride ?? routeFarmId ?? ''

  return useQuery({
    queryKey: getPondKey.names(farmId),
    queryFn: async () => {
      const { data } = await getAllPondNames(farmId)
      return data.data
    },
    staleTime: Infinity,
    enabled: !!farmId,
  })
}
